import React, { useState } from 'react';
import { Plus, Loader2, X, CheckCircle } from 'lucide-react';
import { useToast } from '../contexts/ToastContext';
import { AssociateData } from '../services/otpAuthService';
import { requestIntegrativa } from '../services/associatePaymentService';

interface AssociateIntegrativaRequestProps {
  associate: AssociateData;
  /** Settori con un pagamento integrativa già in attesa: vanno esclusi per evitare richieste doppie */
  pendingSettori?: string[];
  onClose: () => void;
}

const SETTORI = ['STUDIO', 'AUTOSCUOLA', 'NAUTICA'] as const;

const SETTORI_LABELS: Record<string, string> = {
  STUDIO: 'Studio di Consulenza',
  AUTOSCUOLA: 'Autoscuola',
  NAUTICA: 'Nautica'
};

const AssociateIntegrativaRequest: React.FC<AssociateIntegrativaRequestProps> = ({
  associate,
  pendingSettori = [],
  onClose
}) => {
  const toast = useToast();
  const [selectedSettore, setSelectedSettore] = useState('');
  const [processing, setProcessing] = useState(false);
  const [success, setSuccess] = useState(false);

  // Escludi categoria principale e integrative gia presenti
  const categoriaUpper = (associate.categoria || '').toUpperCase();
  const integrativeEsistenti = (associate.integrativa || '')
    .split(' ')
    .map(s => s.trim().toUpperCase())
    .filter(Boolean);

  const pendingUpper = pendingSettori.map(s => (s || '').toUpperCase());

  const settoriDisponibili = SETTORI.filter(
    s => s !== categoriaUpper && !integrativeEsistenti.includes(s) && !pendingUpper.includes(s)
  );

  const handleSubmit = async () => {
    if (!selectedSettore) {
      toast.error('Seleziona un settore');
      return;
    }

    setProcessing(true);
    try {
      await requestIntegrativa(selectedSettore);
      setSuccess(true);
      toast.success('Richiesta inviata alla segreteria');
    } catch (error: any) {
      console.error('Errore richiesta integrativa:', error);
      toast.error(error.response?.data?.message || 'Errore durante la richiesta');
    } finally {
      setProcessing(false);
    }
  };

  if (success) {
    return (
      <div className="p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
        <div className="flex items-start gap-2">
          <CheckCircle className="w-4 h-4 text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm text-green-800 dark:text-green-200">
              Richiesta inviata. La segreteria verifichera e abilitera il pagamento.
            </p>
            <button
              onClick={onClose}
              className="text-xs text-green-600 dark:text-green-400 hover:underline mt-1"
            >
              Chiudi
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (settoriDisponibili.length === 0) {
    return (
      <div className="p-3 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg">
        <p className="text-xs text-gray-500 dark:text-gray-400">
          Tutte le categorie integrative sono gia attive.
        </p>
        <button
          onClick={onClose}
          className="text-xs text-gray-400 hover:underline mt-1"
        >
          Chiudi
        </button>
      </div>
    );
  }

  return (
    <div className="p-3 bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-lg">
      <div className="flex items-center justify-between mb-2">
        <p className="text-xs font-semibold text-purple-800 dark:text-purple-200">
          Richiedi quota integrativa
        </p>
        <button
          onClick={onClose}
          className="p-0.5 text-purple-400 hover:text-purple-600 dark:hover:text-purple-300 transition-colors"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>

      <div className="flex gap-2">
        <select
          value={selectedSettore}
          onChange={(e) => setSelectedSettore(e.target.value)}
          className="flex-1 px-2 py-1.5 text-sm border border-purple-200 dark:border-purple-700 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500"
        >
          <option value="">Seleziona settore...</option>
          {settoriDisponibili.map(s => (
            <option key={s} value={s}>{SETTORI_LABELS[s] || s}</option>
          ))}
        </select>

        <button
          onClick={handleSubmit}
          disabled={processing || !selectedSettore}
          className="flex items-center gap-1 px-3 py-1.5 text-xs font-semibold text-white bg-purple-600 hover:bg-purple-700 disabled:bg-gray-400 rounded transition-colors"
        >
          {processing ? (
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
          ) : (
            <Plus className="w-3.5 h-3.5" />
          )}
          Richiedi
        </button>
      </div>
    </div>
  );
};

export default AssociateIntegrativaRequest;
